import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { RuntimeService } from '../../shared/services/runtime.service';
import { SyncService, isSyncComplete } from '../../shared/services/sync.service';
import { AuthService } from '../services/auth.service';

// Desktop-only: first-time devices must finish /data-sync before entering the app.
// Returning devices already have local master data (isFirstTimeSetupCompleted) and
// may continue if this login's download fails, is interrupted, or the status call
// cannot be reached — otherwise send them back through /data-sync.
export const syncStatusGuard: CanActivateFn = () => {
  const runtime = inject(RuntimeService);
  const router = inject(Router);
  const syncService = inject(SyncService);
  const authService = inject(AuthService);

  if (!runtime.IsDesktopApplication) {
    return true;
  }

  const canProceedWithoutThisSync = authService.hasCompletedFirstTimeSetup();

  return syncService.getSyncStatus().pipe(
    map((res) => {
      const data = res.data;
      // overallLastSyncTime is only a display timestamp (see flight-list's "last synced"
      // label) and has been observed to still be null right after a sync that itself
      // completed successfully — fall back to the run's own completion status so that
      // case doesn't bounce the user back to /data-sync in a loop.
      const isSynced = !!data.overallLastSyncTime || isSyncComplete(data);
      if (isSynced || canProceedWithoutThisSync) {
        return true;
      }
      return router.createUrlTree(['/data-sync']);
    }),
    catchError(() =>
      canProceedWithoutThisSync ? of(true) : of(router.createUrlTree(['/data-sync']))
    )
  );
};
